import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Tag} from '@/components/ui/tag'
import {ActionEficaciaBlock} from '@/components/action-eficacia-block'
import {ActionEvidenceBlock} from '@/components/action-evidence-block'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {NOTAS_PAGE_SIZE} from '@/hooks/sgsst/evaluacion/use-item-notes'
import {ActionNotesThread} from '@/components/action-notes-thread'
import {mintUploadTarget, rutaDeDescarga, uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  useActionEvidence,
  useActionNotes,
  useAddActionNote,
  useAttachActionEvidence,
  useDeleteActionNote,
  type SgsstAction,
  type SgsstActionStatus,
} from '@/hooks/sgsst/plan/use-actions'

const STATUS_TONE: Record<SgsstActionStatus, 'neutral' | 'amber' | 'green'> = {
  pendiente: 'neutral',
  en_curso: 'amber',
  cumplida: 'green',
}

/** `eficaciaEditable` es una puerta aparte: la eficacia se verifica con el año ya sellado y
 * cuelga de `sgsst_firmar` (un juicio legal), no del permiso de operar el plan. */
export function ActionDetailSheet({
  businessId,
  action,
  editable,
  eficaciaEditable,
  canOperate,
  open,
  onOpenChange,
  onAnalizarCausa,
}: {
  businessId: string
  action: SgsstAction | null
  editable: boolean
  eficaciaEditable: boolean
  canOperate: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  /** El diálogo lo monta la página porque el aviso de sugerencia también lo abre. */
  onAnalizarCausa: (action: SgsstAction) => void
}) {
  const {t} = useTranslation()
  const actionId = action?.id
  // Cada lista pagina aparte y vuelve a la página 1 al agregar: lo nuevo entra arriba.
  const [notesPage, setNotesPage] = useState(1)
  const [evidencePage, setEvidencePage] = useState(1)
  const {data: notes} = useActionNotes(open ? businessId : undefined, actionId, notesPage)
  const addNote = useAddActionNote(businessId, actionId ?? '')
  const deleteNote = useDeleteActionNote(businessId, actionId ?? '')
  const hasStandard = open && !!action?.standard_code
  const {data: evidence} = useActionEvidence(hasStandard ? businessId : undefined, actionId, evidencePage)
  const attach = useAttachActionEvidence(businessId, actionId ?? '')

  if (!action) return null

  async function upload(file: File, validateAi: boolean, onProgress: (pct: number) => void) {
    const target = await mintUploadTarget(businessId, file)
    await uploadToStorage(target, file, onProgress)
    await attach.mutateAsync({
      object_key: target.object_key,
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      validate_ai: validateAi,
    })
    setEvidencePage(1)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="action-detail-sheet">
        <DialogHeader>
          <DialogTitle>{t('nota.sheetTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 text-sm text-foreground">{action.description}</p>
          <Tag tone={STATUS_TONE[action.status]}>{t(`plan.status.${action.status}`)}</Tag>
        </div>

        {action.status === 'cumplida' && (
          <ActionEficaciaBlock
            businessId={businessId}
            action={action}
            editable={eficaciaEditable}
            canOperate={canOperate}
          />
        )}

        <div className="surface-divider flex flex-wrap items-center gap-2.5 rounded-xl border border-dashed bg-secondary/30 px-3.5 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{t('analisis.titulo')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {action.tiene_analisis ? t('analisis.ficha.abierto') : t('analisis.ficha.invitacion')}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            data-testid="analisis-abrir-ficha"
            onClick={() => onAnalizarCausa(action)}>
            {action.tiene_analisis ? t('analisis.ficha.ver') : t('analisis.ficha.abrir')}
          </Button>
        </div>

        <div>
          <h3 className="mb-2 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('nota.notesTitle')}
          </h3>
          <ActionNotesThread
            notes={notes?.items ?? []}
            editable={editable}
            adding={addNote.isPending}
            deleteNote={deleteNote}
            onAdd={async (text) => {
              await addNote.mutateAsync(text)
              setNotesPage(1)
            }}
          />
          {notes && notes.total > 0 && (
            <PaginationBar
              page={notesPage}
              pageSize={NOTAS_PAGE_SIZE}
              total={notes.total}
              onPageChange={setNotesPage}
            />
          )}
        </div>

        {action.standard_code && (
          <div>
            <h3 className="mb-2 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('nota.evidenceTitle')}
            </h3>
            <ActionEvidenceBlock
              businessId={businessId}
              rutaDeDescarga={(evidenceId) => rutaDeDescarga(businessId, evidenceId)}
              items={evidence?.items ?? []}
              editable={editable}
              showAiToggle
              onUpload={upload}
            />
            {evidence && evidence.total > 0 && (
              <PaginationBar
                page={evidencePage}
                pageSize={NOTAS_PAGE_SIZE}
                total={evidence.total}
                onPageChange={setEvidencePage}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
